/**
 * Hex colours, to numbers.
 *
 * **Its own module because it must be testable, and the tests here run under Node with no DOM.**
 * `tsconfig.scripts.json` typechecks `src/**` + `/*.test.ts` with `lib: ES2022` and no DOM globals, so
 * a test may only reach code that touches neither `document` nor `window`. This parser touches
 * neither; `paint.ts`, which reads the tokens off the document, touches both. Leaving the parser
 * there would have meant either no test for it or DOM types in the Node config.
 */

/**
 * `#rgb` and `#rrggbb`, to a number. `null` when it is neither.
 *
 * **The short form is here because of the minifier, not because anybody writes it.** `app.css` spells
 * every colour in full, so a six-digit-only parser looked sufficient — and was, until a production
 * build. Lightning CSS shortens what it can, and `--color-card: #ffffff` is the one entry in that
 * palette with a shorter spelling: it ships as `#fff`. So the canvas booted in development and threw
 * on the deployed site, on exactly one token, and the failure named tree-shaking — the cause of the
 * *previous* bug in `paint.ts` — while the variable had been present the whole time.
 *
 * Any colour whose pairs repeat does the same, so this handles the form rather than that one colour.
 */
export function parseHex(raw: string): number | null {
  const long = /^#([0-9a-f]{6})$/i.exec(raw);
  if (long?.[1]) return Number.parseInt(long[1], 16);

  const short = /^#([0-9a-f]{3})$/i.exec(raw);
  if (short?.[1]) return Number.parseInt(short[1].replace(/./g, (digit) => digit + digit), 16);

  return null;
}
